export class TabSessionManager {
  private static tabId: string;
  private static heartbeatTimer: any = null;

  private static TAB_ID_KEY = 'currentTabId';
  private static SESSION_KEY = 'currentSession';
  private static TOKEN_KEY = 'jhi-authenticationToken';
  private static REMEMBER_KEY = 'RememberMe';
  private static TAB_REGISTRY_KEY = 'tabRegistry';
  private static heartbeatInterval: any = null;

  // Optimized intervals
  private static HEARTBEAT_INTERVAL = 5000; // 5 seconds when active
  private static HEARTBEAT_INTERVAL_HIDDEN = 30000; // 30 seconds when hidden
  private static TAB_TIMEOUT = 15000; // Consider dead after 15 seconds

  // Initialize or recover tab
  static initializeTab(): string {
    // Generate unique tab ID
    this.tabId = sessionStorage.getItem(this.TAB_ID_KEY) || `tab_${Date.now()}_${Math.random()}`;
    sessionStorage.setItem(this.TAB_ID_KEY, this.tabId);

    // Initial registration
    this.updateHeartbeat();

    // Start optimized heartbeat
    this.startOptimizedHeartbeat();

    // Listen for storage events
    window.addEventListener('storage', this.handleStorageEvent);

    // Cleanup on close
    window.addEventListener('beforeunload', () => this.onTabClose());

    // Optimize based on visibility
    document.addEventListener('visibilitychange', () => {
      this.handleVisibilityChange();
    });

    return this.tabId;
  }

  private static startOptimizedHeartbeat(): void {
    // Clear existing timer
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }

    // Use different intervals based on tab visibility
    const interval = document.hidden ? this.HEARTBEAT_INTERVAL_HIDDEN : this.HEARTBEAT_INTERVAL;

    this.heartbeatTimer = setInterval(() => {
      this.updateHeartbeat();
      this.cleanupDeadTabs();
    }, interval);
  }

  private static updateHeartbeat(): void {
    const tabs = this.getTabRegistry();
    tabs[this.tabId] = Date.now();

    try {
      localStorage.setItem(this.TAB_REGISTRY_KEY, JSON.stringify(tabs));
    } catch (e) {
      // Handle quota exceeded
      console.error('Failed to update heartbeat:', e);
      this.cleanupDeadTabs(true); // Force aggressive cleanup
    }
  }

  private static getTabRegistry(): Record<string, number> {
    try {
      return JSON.parse(localStorage.getItem(this.TAB_REGISTRY_KEY) || '{}');
    } catch {
      return {};
    }
  }

  private static cleanupDeadTabs(aggressive = false): void {
    const tabs = this.getTabRegistry();
    const now = Date.now();
    const timeout = aggressive ? 5000 : this.TAB_TIMEOUT;

    let hasChanges = false;
    Object.keys(tabs).forEach(tabId => {
      if (tabId !== this.tabId && now - tabs[tabId] > timeout) {
        delete tabs[tabId];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      localStorage.setItem(this.TAB_REGISTRY_KEY, JSON.stringify(tabs));
    }
  }

  // Get active tab count
  static getActiveTabCount(): number {
    this.cleanupDeadTabs();
    const registry = this.getTabRegistry();
    return Object.keys(registry).length;
  }

  // Handle tab close (called on beforeunload)
  public static onTabClose(): void {
    const rememberMe = localStorage.getItem(this.REMEMBER_KEY);

    if (rememberMe === '0') {
      const tabs = this.getTabRegistry();
      delete tabs[this.tabId];

      // Check if last tab
      if (Object.keys(tabs).length === 0) {
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.SESSION_KEY);
      } else {
        // Update registry without this tab
        localStorage.setItem(this.TAB_REGISTRY_KEY, JSON.stringify(tabs));
      }
    }

    // Clear timer
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
  }

  static startHeartbeat(): void {
    const tabId = sessionStorage.getItem(this.TAB_ID_KEY);
    if (!tabId) return;

    // Clear existing interval to prevent memory leak
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Start new interval
    this.heartbeatInterval = setInterval(() => {
      this.updateHeartbeat();
      this.cleanupDeadTabs();
    }, 2000);

    // Initial heartbeat
    this.updateHeartbeat();
  }

  static stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  static syncToken(): void {
    const sessionToken = sessionStorage.getItem(this.TOKEN_KEY);
    const localToken = localStorage.getItem(this.TOKEN_KEY);

    if (!sessionToken && localToken) {
      sessionStorage.setItem(this.TOKEN_KEY, localToken);
      if (localStorage.getItem(this.SESSION_KEY)) {
        sessionStorage.setItem(this.SESSION_KEY, localStorage.getItem(this.SESSION_KEY));
      }
    } else if (sessionToken && !localToken && localStorage.getItem(this.REMEMBER_KEY) === '0') {
      localStorage.setItem(this.TOKEN_KEY, sessionToken);
      if (sessionStorage.getItem(this.SESSION_KEY)) {
        localStorage.setItem(this.SESSION_KEY, sessionStorage.getItem(this.SESSION_KEY));
      }
    }
  }

  // Check if session valid
  static isSessionValid(): boolean {
    return !!(sessionStorage.getItem(this.TOKEN_KEY) || localStorage.getItem(this.TOKEN_KEY));
  }

  // Handle visibility change
  public static handleVisibilityChange(): void {
    if (!document.hidden) {
      // Tab became visible - update immediately and restart heartbeat
      this.updateHeartbeat();
      this.startOptimizedHeartbeat();
      this.syncToken();
    } else {
      // Tab hidden - reduce heartbeat frequency
      this.startOptimizedHeartbeat();
    }
  }

  static resetAll(): void {
    localStorage.removeItem(this.TAB_REGISTRY_KEY);
    this.stopHeartbeat();
    console.warn('Tab manager reset');
  }

  private static handleStorageEvent = (event: StorageEvent): void => {
    // Handle changes to localStorage from other tabs
    if (event.key === this.TAB_REGISTRY_KEY) {
      // Tab registry changed from another tab
      this.cleanupDeadTabs();
    } else if (event.key === this.TOKEN_KEY) {
      // Token changed from another tab
      if (event.newValue === null) {
        // Token was removed - logout this tab too
        this.handleTokenRemoval();
      } else if (event.newValue && !sessionStorage.getItem(this.TOKEN_KEY)) {
        // Token was added - sync to this tab
        this.syncToken();
      }
    } else if (event.key === this.SESSION_KEY) {
      // Session changed from another tab
      if (event.newValue) {
        sessionStorage.setItem(this.SESSION_KEY, event.newValue);
      } else {
        sessionStorage.removeItem(this.SESSION_KEY);
      }
    } else if (event.key === this.REMEMBER_KEY) {
      // Remember me setting changed
      if (event.newValue === '0') {
        // Remember me disabled - clean up if this is the last tab
        const activeTabCount = this.getActiveTabCount();
        if (activeTabCount <= 1) {
          localStorage.removeItem(this.TOKEN_KEY);
          localStorage.removeItem(this.SESSION_KEY);
        }
      }
    }
  };

  private static handleTokenRemoval(): void {
    // Remove session token and redirect to login
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.SESSION_KEY);

    // Optional: Trigger logout event or redirect
    window.location.href = '/login';
  }
}
